import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type ToastType = 'success' | 'error' | 'warning' | 'info';
interface Toast { id: string; type: ToastType; message: string; }
interface ToastCtx { toast: (msg: string, type?: ToastType) => void; }

const ToastContext = createContext<ToastCtx>({ toast: () => {} });
export const useToast = () => useContext(ToastContext);

const ICONS = {
  success: CheckCircle2,
  error:   XCircle,
  warning: AlertCircle,
  info:    Info,
};
const COLORS = {
  success: { bg: '#22c55e', icon: '#ffffff', text: '#ffffff' },
  error:   { bg: '#ef4444', icon: '#ffffff', text: '#ffffff' },
  warning: { bg: '#f59e0b', icon: '#ffffff', text: '#ffffff' },
  info:    { bg: '#3b82f6', icon: '#ffffff', text: '#ffffff' },
};

function ToastItem({ t, onDismiss }: { t: Toast; onDismiss: (id: string) => void }) {
  const Icon = ICONS[t.type];
  const c = COLORS[t.type];
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(t.id), 3800);
    return () => clearTimeout(timer);
  }, [t.id, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 60, scale: 0.92 }}
      animate={{ opacity: 1, x: 0,  scale: 1 }}
      exit={{    opacity: 0, x: 60, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 450, damping: 35 }}
      className="relative flex items-center gap-3 px-5 py-4 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] min-w-[320px] max-w-[440px] overflow-hidden"
      style={{ background: c.bg }}
    >
      {/* Progress Bar */}
      <motion.div 
        initial={{ width: '100%' }}
        animate={{ width: 0 }}
        transition={{ duration: 3.8, ease: 'linear' }}
        className="absolute bottom-0 left-0 h-1 bg-white/20"
      />

      <Icon className="w-5 h-5 shrink-0" style={{ color: c.icon }} />
      <p className="text-[14px] font-bold flex-1 leading-tight" style={{ color: c.text }}>{t.message}</p>
      <button onClick={() => onDismiss(t.id)} className="shrink-0 transition-opacity hover:opacity-75 relative z-10">
        <X className="w-4 h-4" style={{ color: c.icon }} />
      </button>
    </motion.div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: string) => setToasts(p => p.filter(t => t.id !== id)), []);
  const toast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(p => [...p.slice(-4), { id, type, message }]);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence mode="sync">
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem t={t} onDismiss={dismiss} />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
